/* eslint-disable max-lines */
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  type InputSignal,
  output,
  OutputEmitterRef,
  Renderer2,
  signal,
  type SimpleChanges,
  ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, EMPTY, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  skip,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import type { Attachment } from '@core/attachments-types';
import { AttachmentItemComponent } from '@core/attachments-ui';
import {
  filterBoolean,
  formDetectChanges,
  LocalStorageService,
  RtComponentOutlet,
  type UnwrapInputs,
} from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  BaseNote,
  NoteDeleteAttachmentEvent,
  NoteEditEvent,
  NoteReplyChangesEvent,
  NoteSave,
  NoteUploadAttachmentEvent,
} from '@core/notes-types';
import type {
  ConfigPlugins,
  EditorButton,
  MentionElement,
  PluginsAPI,
} from '@core/prosemirror-legacy';
import {
  EmojiPluginPickerBeforeOpen,
  EmojiPluginPickerClose,
  LinkEditBeforeOpen,
  LinkEditClose,
  MentionsPluginPickerBeforeOpen,
  MentionsPluginPickerClose,
} from '@core/prosemirror-legacy';
import type { ProsemirrorDefaultConfigEvents } from '@core/prosemirror-legacy/webapp';
import {
  ProsemirrorDefaultConfig,
  ProsemirrorGroupedConfig,
  ProsemirrorGroupedConfigDirective,
  WysiwygComponent,
} from '@core/prosemirror-legacy/webapp';
import type { EditorJSONState } from '@core/prosemirror-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DividerComponent,
  GroupComponent,
  NotificationsController,
  SpinnerComponent,
} from '@core/user-interface-legacy';
import { getHumanReadableFileSize, MaxFileSize, validateMaxFileSize } from '@core/utils-legacy';
import type { Visibility } from '@core/visibility-types';
import { DefaultVisibility, VisibilityLevel } from '@core/visibility-types';

import { injectNotesTranslations } from '../helpers';
import { NotesAnalytics } from '../notes.analytics';

export type VisibilitySelector = new (...args: any[]) => {
  visibility: InputSignal<Visibility>;
  avatarMaxLength: InputSignal<number>;
  wrapOnSmallerResolutions: InputSignal<boolean>;
  candidateId: InputSignal<number | undefined>;
  groupUsersByAccess: InputSignal<boolean | undefined | null>;
  visibilityChanges: OutputEmitterRef<Visibility>;
};

export type VisibilitySelectorInputs = UnwrapInputs<
  InstanceType<VisibilitySelector>,
  | 'visibility'
  | 'avatarMaxLength'
  | 'wrapOnSmallerResolutions'
  | 'candidateId'
  | 'groupUsersByAccess'
>;

@Component({
  selector: 'rt-note-form',
  templateUrl: './note-form.component.html',
  styleUrls: ['./note-form.component.less'],
  host: {
    '[class.context-private]': 'visibility() && visibility()?.level !== VisibilityLevel.Public',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideAnalyticsViewContext(AnalyticsViewContext.Notes), NotesAnalytics],
  imports: [
    DividerComponent,
    SpinnerComponent,
    AttachmentItemComponent,
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    ReactiveFormsModule,
    TranslatePipe,
    NgTemplateOutlet,
    WysiwygComponent,
    ProsemirrorGroupedConfigDirective,
    RtComponentOutlet,
  ],
})
export class NoteFormComponent<Note extends BaseNote<Note>> {
  private readonly defaultVisibility: Visibility = DefaultVisibility;
  private readonly localStorageService = inject(LocalStorageService);
  private readonly element = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly i18n = inject(I18nService);
  private readonly notificationsController = inject(NotificationsController);

  protected readonly notesTranslations = injectNotesTranslations();

  public readonly canDeleteAttachments = input<boolean>(false);
  public readonly replyToId = input<number>(undefined!);
  public readonly placeholder = input<string>('');
  public readonly autofocus = input<boolean>(false);
  public readonly autosave = input<boolean>(false);
  public readonly attachmentsTemp = input<
    Array<{
      uuid: string;
      attachment: Attachment;
    }>
  >([]);
  public readonly attachmentLoading = input<boolean>(false);
  public readonly noteSaveLoading = input<boolean>(false);
  public readonly noteHasError = input<boolean>(false);
  public readonly allNotesFlat = input<Note[]>(undefined!);

  public note = input<Note>();
  public localStorageKeyBuilder = input.required<(params: { parentNote: Note }) => string>();
  public noteToMention = input.required<(note: Note) => MentionElement | null>();
  public active = input<boolean>(false);
  public keepFocus = signal<boolean>(false);
  public value = signal<EditorJSONState | null>(null);
  public savedValue = signal<EditorJSONState | null>(null);
  public formTouched = signal<boolean>(false);
  public attachmentsDirty = signal<boolean>(false);
  public visibility = signal<Visibility>(this.defaultVisibility);
  public localStorageKey = computed(() =>
    this.localStorageKeyBuilder()({ parentNote: this.note()! }),
  );
  public hasFileUpload = input(true);

  public changeNoteVisibility = output<boolean>();
  public typingStart = output<void>();
  public typingFinish = output<void>();
  public cancelClick = output<Note>();
  public noteSaveClick = output<NoteSave<Note>>();
  public noteEditClick = output<NoteEditEvent>();
  public replyChanges = output<NoteReplyChangesEvent>();

  public uploadAttachmentClick = output<NoteUploadAttachmentEvent>();
  public deleteAttachmentClick = output<NoteDeleteAttachmentEvent<Note>>();

  @ViewChild('fileInput', { static: true }) public fileInput: ElementRef<HTMLInputElement>;

  public visibilitySelectorComponent = input<VisibilitySelector>();
  public visibilityComponentInputs = input<Partial<VisibilitySelectorInputs>>();

  public resolvedVisibilitySelectorInputs = computed(() => ({
    avatarMaxLength: 1,
    visibility: this.visibility(),
    wrapOnSmallerResolutions: this.isSmallResolution(),
    ...this.visibilityComponentInputs(),
  }));
  public isSmallResolution = toSignal(
    this.breakpointObserver.observe(['(max-width: 1439px)']).pipe(map(state => state.matches)),
    { initialValue: false },
  );
  public wysiwygControl = new UntypedFormControl();
  public actionButtons = computed((): EditorButton[] =>
    this.hasFileUpload()
      ? [
          {
            icon: 'attach-file',
            tooltipText: this.notesTranslations.addAttachment,
            isActive: () => false,
            run: () => this.fileInput.nativeElement.click(),
          },
        ]
      : [],
  );

  public VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;
  public ProsemirrorDefaultConfig = ProsemirrorDefaultConfig;
  public ProsemirrorGroupedConfig = ProsemirrorGroupedConfig;

  private visibility$ = toObservable(this.visibility);
  private wysiwygComponent$ =
    new BehaviorSubject<WysiwygComponent<ProsemirrorDefaultConfig> | null>(null);
  private ngOnDestroy$ = new Subject<void>();

  @ViewChild(WysiwygComponent)
  private set wysiwygComponent(value: WysiwygComponent<ProsemirrorDefaultConfig>) {
    if (value === this.wysiwygComponent$.value) return;

    this.wysiwygComponent$.next(value);
  }

  public ngOnInit(): void {
    const note = this.note();
    if (note && note.bodyJson) {
      this.visibility.set(note.visibility);
    }

    if (this.autosave() && !(note && note.bodyJson)) {
      this.restoreNoteContents();

      this.wysiwygControl.valueChanges
        .pipe(debounceTime(300), takeUntil(this.ngOnDestroy$))
        .subscribe(() => this.saveNoteContents());
    }

    this.visibility$
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.ngOnDestroy$))
      .subscribe(visibility => {
        const isPrivateVisibilityFlag = visibility?.level !== VisibilityLevel.Public;
        this.changeNoteVisibilityClass(isPrivateVisibilityFlag);
        this.changeNoteVisibility.emit(isPrivateVisibilityFlag);
      });

    this.wysiwygControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(value => this.onValueChange(value));

    this.wysiwygComponent$
      .pipe(
        filterBoolean,
        switchMap(wysiwyg =>
          wysiwyg.editorApi$.pipe(
            switchMap((api: PluginsAPI<ConfigPlugins<ProsemirrorDefaultConfig>>) =>
              api ? of({ wysiwyg, api }) : EMPTY,
            ),
          ),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(({ api, wysiwyg }) => {
        if (this.replyToId() && wysiwyg) {
          const { mentionsService } = api;
          const parentNote = this.allNotesFlat().find(({ id }) => id === this.replyToId());
          const mention = parentNote && this.noteToMention()(parentNote);
          if (parentNote && mention) {
            const state = mentionsService.insertMentionAtSelection(mention);
            this.wysiwygControl.setValue(state.toJSON());
          }
        }
      });

    formDetectChanges(this.wysiwygControl, this.cdRef, this.ngOnDestroy$);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const hasError = changes.noteHasError ? changes.noteHasError.currentValue : this.noteHasError();

    if (changes.noteSaveLoading && !changes.noteSaveLoading.firstChange) {
      if (!changes.noteSaveLoading.currentValue && !hasError) {
        this.resetForm();
      }
    }

    if ('note' in changes) {
      this.attachmentsDirty.set(false);
      this.wysiwygControl.setValue(this.note()!.bodyJson);
    }
  }

  public ngAfterContentInit(): void {
    const textarea = this.element.nativeElement.querySelector('textarea');

    if (this.autofocus() && textarea) {
      textarea.focus();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();

    if (this.active()) {
      this.typingFinish.emit();
    }
  }

  public uploadAttachment(files: FileList | null, fileInput: HTMLInputElement): void {
    if (files && files.length > 0) {
      const file = files[0];
      const validSize = validateMaxFileSize(file, MaxFileSize.File50MB);

      if (!validSize) {
        const message = this.i18n.translate(this.notesTranslations.fileSizeLimitExceeded, {
          limit: getHumanReadableFileSize(MaxFileSize.File50MB),
          fileSize: getHumanReadableFileSize(file.size),
        });
        this.notificationsController.error(message);

        return;
      }

      const note = this.note();
      this.uploadAttachmentClick.emit({
        attachment: {
          file,
        },
        note: {
          replyToId: this.replyToId(),
          id: (note && note.id) || null,
        },
      });

      this.attachmentsDirty.set(true);
    }
    // @ts-ignore
    fileInput.value = null;
  }

  public deleteAttachment(attachment: Attachment): void {
    this.deleteAttachmentClick.emit({
      note: this.note()!,
      attachmentId: attachment.id,
    });
  }

  public onValueChange(data: EditorJSONState): void {
    if (data !== this.value()) {
      this.value.set(data);
      this.typingStart.emit();
    }
    const replyToId = this.replyToId();
    if (replyToId) {
      this.replyChanges.emit({
        value: data,
        replyToId: replyToId,
      });
    }
  }

  public onFocusChange(focused: boolean): void {
    if (focused) {
      this.typingStart.emit();
      this.noteEditClick.emit({ id: this.note()!.id! });
    } else {
      if (
        this.wysiwygControl &&
        this.wysiwygControl.pristine &&
        !this.wysiwygControl.value &&
        !this.keepFocus() &&
        !this.attachmentsDirty() &&
        !this.hasAttachments()
      ) {
        this.cancel();
      }
    }
  }

  public onEditorEvents(event: ProsemirrorDefaultConfigEvents) {
    if (
      event instanceof EmojiPluginPickerBeforeOpen ||
      event instanceof MentionsPluginPickerBeforeOpen ||
      event instanceof LinkEditBeforeOpen
    ) {
      this.setKeepFocus();
    } else if (
      event instanceof EmojiPluginPickerClose ||
      event instanceof MentionsPluginPickerClose ||
      event instanceof LinkEditClose
    ) {
      this.keepFocus.set(false);
    }
  }

  public setKeepFocus(): void {
    this.keepFocus.set(true);
  }

  public isValid(): boolean {
    return !!this.wysiwygControl.value;
  }

  public hasVisibilityOptions(): boolean {
    if (this.replyToId()) {
      return false;
    }

    const note = this.note();
    return note && note.hasOwnProperty('replyToId') ? !note.replyToId : true;
  }

  public cancel(): void {
    this.resetForm();
    this.cancelClick.emit(this.note()!);
    this.typingFinish.emit();
  }

  public save(): void {
    if (!this.isValid()) {
      return;
    }

    this.typingFinish.emit();

    const currentNote = this.note();
    const note = <const>{
      id: (currentNote && currentNote.id!) || null,
      replyToId: this.replyToId() || (currentNote && currentNote.replyToId) || null,
      visibility: this.visibility(),
      bodyJson: this.wysiwygControl.value,
    };

    const attachmentIds = this.getAttachmentIds();

    const payload = {
      note: note,
      attachmentIds,
    };
    this.replyChanges.emit({ value: null, replyToId: this.replyToId() });
    this.noteSaveClick.emit(payload);
  }

  public changeNoteVisibilityClass(isPrivate: boolean): void {
    isPrivate
      ? this.renderer.addClass(this.element.nativeElement, 'context-private')
      : this.renderer.removeClass(this.element.nativeElement, 'context-private');
  }

  public onVisibilityChanges = (visibility: Visibility) => this.visibility.set(visibility);

  private hasAttachments(): boolean {
    let result = false;

    const note = this.note();
    if (note) {
      result = note.attachments ? note.attachments.length > 0 : false;
    }

    const attachmentsTemp = this.attachmentsTemp();
    if (!result && attachmentsTemp) {
      result = attachmentsTemp.length > 0;
    }

    return result;
  }

  private getAttachmentIds(): number[] {
    const note = this.note();
    const attachmentsTemp = this.attachmentsTemp();
    return [
      ...(attachmentsTemp ? attachmentsTemp : []).map(({ attachment }) => attachment.id),
      ...(note ? note.attachments || [] : []).map(attachment => attachment.id),
    ];
  }

  private restoreNoteContents(): void {
    const savedValue = this.localStorageService.get(this.localStorageKey());

    if (savedValue) {
      this.noteEditClick.emit({ id: this.note()!.id! });
      this.value.set(savedValue);
      this.savedValue.set(savedValue);
      this.wysiwygControl.setValue(savedValue);
    }
  }

  private saveNoteContents(): void {
    if (this.value() && this.value() !== this.savedValue()) {
      const savedValue = this.wysiwygControl.value;
      this.localStorageService.set(this.localStorageKey(), savedValue);
      this.savedValue.set(savedValue);
    }
  }

  private clearNoteContents(): void {
    this.localStorageService.remove(this.localStorageKey());
  }

  private resetForm(): void {
    this.keepFocus.set(false);
    this.value.set(null);
    this.savedValue.set(null);
    this.formTouched.set(false);
    this.attachmentsDirty.set(false);
    this.visibility.set(this.defaultVisibility);

    this.wysiwygControl.reset(null);
    if (this.autosave()) {
      this.clearNoteContents();
    }
  }
}
